import type { TextStyle } from '@shared/data/miroFonts'
import type { LayoutKind } from '@shared/moodboard/layout'
import { MOST_PICTURES, freshSettings, type BoardPicture, type BoardSettings } from './MoodboardBoard'

/**
 * A Mood board card's sheet, kept on the card (Huanrui, 2026-10-16)
 * -------------------------------------------------------------
 * The same as BoardSettings, but every picture named by its KEY rather than
 * its place in a list: the card's pictures are whatever is in the wired
 * frame each time it opens, and a frame changes. A picture still there keeps
 * its place and its cut; one taken out of the frame drops off the sheet; one
 * new to the frame joins the end (while there is room). `known` is every
 * picture the sheet has seen, so one deliberately taken off (Delete) does not
 * come back just because it is still in the frame.
 */
export interface SavedBoard {
  order: string[]
  known: string[]
  frozen: string[] | null
  focus: Record<string, { x: number; y: number; z?: number }>
  kind: LayoutKind
  ground: string
  foot: boolean
  showColours: boolean
  title: string
  keywords: string
  titleStyle: TextStyle
  wordsStyle: TextStyle
  /** The gutter, and the frames' sizes as dragged (2026-10-16; splits.ts). */
  gutter?: number
  splits?: Record<string, number[]>
  /** Quarter turns by picture key; the strip's place and height; Half's share (2026-10-16). */
  turns?: Record<string, number>
  footAt?: 'top' | 'bottom'
  footH?: number
  half?: number | null
  /** The pictures' margin (2026-10-23). Unset: the usual. */
  margin?: number
  /**
   * Lifted pictures (2026-10-23), by picture key like everything else: one
   * still in the frame is lifted again where it was; one gone is dropped.
   */
  floats?: { key: string; x: number; y: number; w: number; angle: number; outline?: number }[]
}

export function toSaved(settings: BoardSettings, pictures: BoardPicture[]): SavedBoard {
  const key = (i: number) => pictures[i]?.src.key ?? ''
  const focus: SavedBoard['focus'] = {}
  for (const [i, at] of Object.entries(settings.focus)) {
    const k = key(Number(i))
    if (k) focus[k] = at
  }
  return {
    order: settings.order.map(key).filter(Boolean),
    known: pictures.map((one) => one.src.key),
    frozen: settings.frozen ? settings.frozen.map(key).filter(Boolean) : null,
    focus,
    kind: settings.kind,
    ground: settings.ground,
    foot: settings.foot,
    showColours: settings.showColours,
    title: settings.title,
    keywords: settings.keywords,
    titleStyle: settings.titleStyle,
    wordsStyle: settings.wordsStyle,
    gutter: settings.gutter,
    splits: settings.splits,
    turns: Object.fromEntries(
      Object.entries(settings.turns ?? {})
        .map(([i, turn]) => [key(Number(i)), turn] as const)
        .filter(([k]) => Boolean(k)),
    ),
    footAt: settings.footAt,
    footH: settings.footH,
    half: settings.half,
    ...(typeof settings.margin === 'number' ? { margin: settings.margin } : {}),
    ...(settings.floats && settings.floats.length > 0
      ? {
          floats: settings.floats
            .map((one) => ({
              key: key(one.pic),
              x: one.x,
              y: one.y,
              w: one.w,
              angle: one.angle,
              ...(typeof one.outline === 'number' ? { outline: one.outline } : {}),
            }))
            .filter((one) => Boolean(one.key)),
        }
      : {}),
  }
}

/**
 * The card's sheet over the pictures as they are now. `flowing` is the order
 * by colour (readPictures), what a card with nothing kept starts from.
 */
export function fromSaved(saved: SavedBoard | undefined, pictures: BoardPicture[], flowing: number[], name?: string): BoardSettings {
  if (!saved || !Array.isArray(saved.order)) return freshSettings(flowing, { title: name })
  const index = new Map(pictures.map((one, i) => [one.src.key, i]))
  const known = new Set(saved.known ?? [])
  // The lifted ones still in the frame, by key (2026-10-23); never in the grid as well.
  const floats: NonNullable<BoardSettings['floats']> = []
  for (const one of saved.floats ?? []) {
    const pic = index.get(one.key)
    if (pic !== undefined && !floats.some((f) => f.pic === pic)) floats.push({
        pic,
        x: one.x,
        y: one.y,
        w: one.w,
        angle: one.angle,
        ...(typeof one.outline === 'number' ? { outline: one.outline } : {}),
      })
  }
  const lifted = new Set(floats.map((one) => one.pic))
  const order = saved.order
    .map((k) => index.get(k))
    .filter((i): i is number => i !== undefined && !lifted.has(i))
  // New to the frame: after the others, in colour order, while there is room.
  for (const i of flowing.concat(pictures.map((_, n) => n))) {
    if (order.length >= MOST_PICTURES) break
    if (!known.has(pictures[i].src.key) && !order.includes(i) && !lifted.has(i)) order.push(i)
  }
  if (order.length === 0) return { ...freshSettings(flowing, { title: name }), ...styleOf(saved) }
  const frozen = saved.frozen ? saved.frozen.map((k) => index.get(k)) : null
  const focus: BoardSettings['focus'] = {}
  for (const [k, at] of Object.entries(saved.focus ?? {})) {
    const i = index.get(k)
    if (i !== undefined) focus[i] = at
  }
  const turns: BoardSettings['turns'] = {}
  for (const [k, turn] of Object.entries(saved.turns ?? {})) {
    const i = index.get(k)
    if (i !== undefined && turn) turns[i] = turn
  }
  // The frames' sizes belong to exactly these pictures: kept only while the
  // frame still holds the same ones.
  const same = order.length === saved.order.length && saved.order.every((k) => index.has(k))
  return {
    ...freshSettings(order),
    ...styleOf(saved),
    splits: same ? saved.splits ?? {} : {},
    turns,
    order,
    floats,
    // Kept only if it still matches the sheet exactly.
    frozen: frozen && frozen.every((i) => i !== undefined) && frozen.length === order.length ? (frozen as number[]) : null,
    focus,
  }
}

function styleOf(saved: SavedBoard): Partial<BoardSettings> {
  return {
    kind: saved.kind,
    ground: saved.ground,
    foot: saved.foot,
    showColours: saved.showColours,
    title: saved.title,
    keywords: saved.keywords,
    titleStyle: saved.titleStyle,
    wordsStyle: saved.wordsStyle,
    ...(typeof saved.gutter === 'number' ? { gutter: saved.gutter } : {}),
    ...(saved.footAt ? { footAt: saved.footAt } : {}),
    ...(typeof saved.footH === 'number' ? { footH: saved.footH } : {}),
    ...(saved.half !== undefined ? { half: saved.half } : {}),
    ...(typeof saved.margin === 'number' ? { margin: saved.margin } : {}),
  }
}
